import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Languages } from "lucide-react";
import api from "../../api";

// Platform admin → Translations → Questionnaires (backend clinical/monitoring/measures_routes.py,
// /api/admin/measures/*). NL Labs keeps one translation per language for every
// clinic, so wording - and results - stay consistent. Patients see the version
// for their communication language. Scoring never changes - only the wording.
const STATUS = { needs_check: "Awaiting check - not shown to patients", built_in: "Built in (official)", approved: "Published", pending_review: "Waiting for supervisor", draft: "Draft", rejected: "Changes requested", retired: "Replaced" };
const RESULT_KEYS = [["minimal", "Result: minimal"], ["mild", "Result: mild"], ["moderate", "Result: moderate"], ["moderately_severe", "Result: moderately severe"], ["severe", "Result: severe"], ["footer", "Result footer"]];

function Row({ label, english, value, onChange, multiline, lang }) {
return (
  <tr className="border-t border-gray-100 align-top">
    <td className="px-3 py-2 w-1/2"><p className="text-[11px] font-semibold text-gray-500">{label}</p><p className="text-sm text-gray-800">{english}</p></td>
    <td className="px-3 py-2 w-1/2">
      {multiline
        ? <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={2} className="w-full rounded-md border border-gray-300 p-2 text-sm" dir={lang === "ur" ? "rtl" : "auto"} />
        : <input value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full rounded-md border border-gray-300 p-2 text-sm" dir={lang === "ur" ? "rtl" : "auto"} />}
    </td>
  </tr>
);
}

export default function MeasureTranslationsCard() {
  const [defs, setDefs] = useState([]);
  const [code, setCode] = useState("");
  const [info, setInfo] = useState(null);
  const [lang, setLang] = useState("hi");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getAdminMeasures().then((r) => { setDefs(r.data || []); setCode((c) => c || r.data?.[0]?.code || ""); }).catch(() => {});
  }, []);

  const loadInfo = () => code && api.getMeasureTranslations(code).then((r) => setInfo(r.data)).catch(() => setInfo(null));
  useEffect(() => { loadInfo(); }, [code]); // eslint-disable-line

  useEffect(() => {
    if (!info) return;
    const existing = info.languages.find((l) => l.code === lang)?.body;
    setForm(existing ? { item_options: {}, ...JSON.parse(JSON.stringify(existing)) } : { name: "", intro: "", items: {}, options: {}, item_options: {}, patient_result: {} });
  }, [info, lang]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.saveMeasureTranslation(code, lang, form);
      toast.success(`Published (version ${r.data.version}) - every clinic's patients get it now`);
      loadInfo();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Couldn't save the translation.");
    } finally {
      setSaving(false);
    }
  };

  const src = info?.source;
  const langInfo = info?.languages.find((l) => l.code === lang);
  const set = (path, key, value) => setForm((f) => (key === null ? { ...f, [path]: value } : { ...f, [path]: { ...(f[path] || {}), [key]: value } }));

  return (
    <div className="border border-gray-200 rounded-lg p-4 md:p-5 bg-gray-50 space-y-3">
      <h3 className="flex items-center gap-2 text-base md:text-lg font-semibold text-gray-800"><Languages className="w-5 h-5 text-primary-600" /> Questionnaire translations</h3>
      <p className="text-xs md:text-sm text-gray-500">One translation per language, used by every clinic. Patients see the version for their communication language; scoring never changes. Use a validated translation wherever one exists - official ones are built in.</p>
      <div className="flex flex-wrap gap-3">
        <label className="normal-case font-sans tracking-normal text-sm"><span className="block text-xs text-gray-600 mb-1">Questionnaire</span>
          <select value={code} onChange={(e) => setCode(e.target.value)} className="rounded-md border border-gray-300 p-2 text-sm">
            {defs.map((d) => <option key={d.code} value={d.code}>{d.name}</option>)}
          </select>
        </label>
        <label className="normal-case font-sans tracking-normal text-sm"><span className="block text-xs text-gray-600 mb-1">Language</span>
          <select value={lang} onChange={(e) => setLang(e.target.value)} className="rounded-md border border-gray-300 p-2 text-sm">
            {(info?.languages || []).map((l) => <option key={l.code} value={l.code}>{l.name}{l.status ? ` - ${STATUS[l.status] || l.status}` : ""}</option>)}
          </select>
        </label>
      </div>
      {langInfo?.status === "rejected" && langInfo.review_note && <p className="text-xs text-rose-700">Supervisor: {langInfo.review_note}</p>}
      {langInfo?.status === "needs_check" && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 space-y-1">
          <p className="font-semibold">Not shown to patients yet.</p>
          <p>{langInfo.source_note}</p>
          <p>Ask a native speaker to compare every line with the {langInfo.official_pdf ? <a href={langInfo.official_pdf} target="_blank" rel="noreferrer" className="underline font-semibold">official PDF</a> : "source"}, correct anything below, then press Save translation to publish it.</p>
        </div>
      )}
      {langInfo?.status === "built_in" && (langInfo.source_note || langInfo.official_pdf) && (
        <p className="text-xs text-emerald-800">{langInfo.source_note} {langInfo.official_pdf && <a href={langInfo.official_pdf} target="_blank" rel="noreferrer" className="underline">Official PDF</a>}</p>
      )}
      {src && form && (
        <>
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="w-full min-w-[640px]">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500"><tr><th className="text-left px-3 py-2">English</th><th className="text-left px-3 py-2">Translation</th></tr></thead>
              <tbody>
                <Row lang={lang} label="Name" english={src.name} value={form.name} onChange={(v) => set("name", null, v)} />
                <Row lang={lang} label="Instructions" english={src.intro} value={form.intro} onChange={(v) => set("intro", null, v)} multiline />
                {src.items.map((i, idx) => [
                  <Row lang={lang} key={i.id} label={`Question ${idx + 1}`} english={i.text} value={form.items?.[i.id]} onChange={(v) => set("items", i.id, v)} multiline />,
                  ...(i.options || []).map((o) => (
                    <Row lang={lang} key={`${i.id}-${o.value}`} label={`Question ${idx + 1} - answer (${o.value})`} english={o.label}
                      value={form.item_options?.[i.id]?.[String(o.value)]}
                      onChange={(v) => setForm((f) => ({ ...f, item_options: { ...(f.item_options || {}), [i.id]: { ...((f.item_options || {})[i.id] || {}), [String(o.value)]: v } } }))} />
                  )),
                ])}
                {src.options.map((o) => <Row lang={lang} key={o.value} label={`Answer (${o.value})`} english={o.label} value={form.options?.[String(o.value)]} onChange={(v) => set("options", String(o.value), v)} />)}
                {RESULT_KEYS.filter(([k]) => src.patient_result?.[k]).map(([k, label]) => (
                  <Row lang={lang} key={k} label={`${label} (optional)`} english={src.patient_result[k]} value={form.patient_result?.[k]} onChange={(v) => set("patient_result", k, v)} multiline />
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-3">
            <button type="button" onClick={save} disabled={saving || !info?.can_edit} className="rounded-lg bg-primary-600 text-white text-sm font-semibold px-4 py-2 disabled:opacity-50">{saving ? "Saving…" : "Save translation"}</button>

          </div>
        </>
      )}
    </div>
  );
}
